import * as React from 'react';
import { CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';
import { palette } from '@seeneco-ui/core/styles';


type WithSoftFetchingOptions<P> = {
	enableCover?: boolean;
	enableSpinner?: boolean;
	style?: CSSProperties;
	shouldUpdateWhen?: (props: P, nextProps: P) => boolean;
};

type RequiredComponentProps = {
	isFetching?: boolean;
	onSetRef?: (ref: any) => void;
};

function withSoftFetching<P = RequiredComponentProps>(options: WithSoftFetchingOptions<P & RequiredComponentProps> = {}) {
	type Props = P & RequiredComponentProps;
	const {
		shouldUpdateWhen = () => false,
		enableCover = false,
		enableSpinner = false,
		style = {},
	} = options;
	return (WrappedComponent: React.ComponentType<Props>) => {
		class UpdateScheduler extends React.Component<Props> {
			static displayName = `withSoftFetching(${WrappedComponent.name || WrappedComponent.displayName})`;
			static defaultProps = {
				onSetRef: () => {},
			};

			shouldComponentUpdate(nextProps: Props) {
				if (nextProps.isFetching) {
					const needUpdate = shouldUpdateWhen(this.props, nextProps);
					if (needUpdate) return nextProps !== this.props;
					return false;
				}

				return nextProps !== this.props;
			}

			render() {
				const { onSetRef } = this.props;

				return (
					<WrappedComponent
						ref={onSetRef}
						{...this.props}
					/>
				);
			}
		}

		type CoverProps = {
			isFetching: boolean;
			isNextUpdateAvailable: boolean;
		};

		const Cover = styled.div`
			position: relative;

			&::after {
				display: block;
				content: '';
				position: absolute;
				z-index: 1;
				top: 0;
				right: 0;
				bottom: 0;
				left: 0;
				pointer-events: none;
				transition: background-color .05s ease-in-out;
			}

			${(p: CoverProps) => p.isFetching && !p.isNextUpdateAvailable && `
				user-select: none;

				&::after {
					pointer-events: all;
					background-color: rgba(255, 255, 255, 0.7);
				}
			`}
		`;

		const pulseMotion = keyframes`
			0%,
			80%,
			100% {
				box-shadow: 0 0;
				height: 4em;
			}
			40% {
				box-shadow: 0 -2em;
				height: 5em;
			}
		`;

		const Spinner = styled.div`
			position: absolute;
			font-size: 8px;
			transform: translateZ(0);
			color: ${palette.accentPrimaryHover};
			background-color: ${palette.accentPrimaryHover};
			animation: ${pulseMotion} 1s infinite ease-in-out;
			width: 1em;
			height: 4em;
			animation-delay: -0.16s;

			&::before,
			&::after {
				position: absolute;
				top: 0;
				content: '';
				background-color: ${palette.accentPrimaryHover};
				animation: ${pulseMotion} 1s infinite ease-in-out;
				width: 1em;
				height: 4em;
			}

			&::before {
				left: -1.5em;
				animation-delay: -0.32s;
			}

			&::after {
				left: 1.5em;
			}
		`;

		const SpinnerLayout = styled.div`
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			z-index: 10;
			opacity: 0.8;
		`;

		class SoftFetching extends React.Component<Props> {
			isNextUpdateAvailable = true;

			componentWillUpdate(nextProps: Props) {
				this.isNextUpdateAvailable = shouldUpdateWhen(this.props, nextProps);
			}

			render() {
				const { isFetching } = this.props;

				if (!enableCover) {
					return (
						<UpdateScheduler style={style} {...this.props as any} />
					);
				}

				const needRenderSpinner = isFetching && enableSpinner && !this.isNextUpdateAvailable;

				return (
					<Cover
						style={style}
						isFetching={isFetching}
						isNextUpdateAvailable={this.isNextUpdateAvailable}>
						{
							needRenderSpinner &&
							<SpinnerLayout>
								<Spinner />
							</SpinnerLayout>
						}
						<UpdateScheduler {...this.props as any} />
					</Cover>
				);
			}
		}

		return SoftFetching;
	}
}

export default withSoftFetching;

